import React, { useMemo, useState } from 'react';
import { Empty, Select } from 'antd';
import moment from 'moment-timezone';
import { startCase } from 'lodash';

import DetailBar from './components/DetailBar';
import SessionsTable from './components/SessionsTable';
import SessionDeviceBreakdown from './components/SessionDeviceBreakdown';
import PerformanceCardSkeleton from '../../components/PerformanceCardSkeleton';
import EntityCardSkeleton from '../../components/EntityCardSkeleton';
import CampaignRangeSelector from '../../components/CampaignRangeSelector';
import DualAxisAreaChart from '../../../../../shared/designSystem/components/DualAxisAreaChart';
import { MenuItemTextColor } from '../../../../../shared/globalStyling/styledText';
import { useTimezone } from '../../../../../../core/hooks/useTimezone';

const { Option } = Select;

// =============================================================================
// SessionsTabView (V1) — facelifted off legacy Row/Col + BizCharts
// `DualAxisChart` (with its built-in date picker) to the v2 BEM scaffold:
//   • `mjl-ds campaign-sessions-v1` outer wrapper
//   • `<section campaign-overview__performance>` housing the unified KPI
//     strip + lifted DateRangePicker + dual-axis trend chart
//   • Device breakdown card below the Performance section
//   • `<section campaign-overview__creatives>`-scoped Sessions table
//
// The picker has been lifted out of the legacy `DualAxisChart` and into
// the Performance card head as a `<CampaignRangeSelector/>` with segmented
// preset pills (matching V1 SignUps / V2 Summary / V2 Display). Its
// onChange wires straight to the controller's `changeDatePicker(dates)`
// so all filter logic (byDate filter + sessionList filter +
// deviceBreakdown recompute) stays byte-for-byte in `SessionsController`.
//
// The Y-axis dropdown options ([Sessions, Page Views]) are now inline
// `<Select/>` controls in the chart head (mirrors B1 BasketItemsBarChart
// pattern). DualAxisAreaChart's `labelToKey` resolves "Sessions" →
// `sessions` and "Page Views" → `pageViews`, both already present on
// `byDate` rows.
// =============================================================================

const TabSkeleton = () => (
  <>
    <PerformanceCardSkeleton />
    <div style={{ marginTop: 10 }}>
      <EntityCardSkeleton />
    </div>
  </>
);

const AXIS_OPTIONS = [
  { key: 'sessions', label: 'Sessions' },
  { key: 'pageViews', label: 'Page Views' },
];

const SessionsTabView = ({
  reportDataSummary,
  campaignOrder,
  changeDatePicker,
  reportDataSummaryLoading,
}) => {
  const { timezone: effectiveTimezone = 'UTC' } = useTimezone();
  document.title = `${campaignOrder.name} - Sessions View`;

  const [leftYLabel, setLeftYLabel] = useState('Sessions');
  const [rightYLabel, setRightYLabel] = useState('Page Views');
  const [byCampaignRangeDate, setByCampaignRangeDate] = useState(null);
  const [rangeType, setRangeType] = useState('Custom');

  // byDate row shape from the controller is `{ date, sessions, pageViews,
  // ... }`. DualAxisAreaChart's XAxis reads `dataKey="xAxis"`, so map
  // `date` → `xAxis` while keeping the metric fields intact for labelToKey.
  const chartRows = useMemo(() => {
    const rows = (reportDataSummary && reportDataSummary.byDate) || [];
    return rows
      .map((row) => ({
        ...row,
        xAxis: moment(row.date).isValid()
          ? moment(row.date).format('YYYY-MM-DD')
          : row.date,
      }))
      .sort((a, b) => moment(a.xAxis).diff(moment(b.xAxis)));
  }, [reportDataSummary]);

  // Picker `defaultDateRange` — clamps to flight dates the same way the
  // legacy chart's `allTimeDateRange` did. Tz-aware (UTC vs LA / etc).
  const defaultDateRange = useMemo(() => {
    if (!campaignOrder || !campaignOrder.startDate || !campaignOrder.endDate) {
      return null;
    }
    return [
      effectiveTimezone === 'UTC'
        ? moment.utc(campaignOrder.startDate)
        : moment.tz(campaignOrder.startDate, effectiveTimezone),
      effectiveTimezone === 'UTC'
        ? moment.utc(campaignOrder.endDate)
        : moment.tz(campaignOrder.endDate, effectiveTimezone),
    ];
  }, [campaignOrder, effectiveTimezone]);

  // Picker change → controller filter. Preserves the `(dates) =>
  // changeDatePicker(dates)` signature the legacy `onDatePickerChange`
  // unwrapped to.
  const handleRangeChange = (v) => {
    setByCampaignRangeDate(v);
    if (v && v.length === 2 && v[0] && v[1]) {
      changeDatePicker(v);
    }
  };

  const sessionListEmpty =
    !reportDataSummary ||
    !reportDataSummary.sessionList ||
    reportDataSummary.sessionList.length === 0;

  if (reportDataSummaryLoading) return <TabSkeleton />;

  return (
    <div className="mjl-ds campaign-sessions-v1">
      {/* Performance section — KPI strip + lifted picker + trend chart. */}
      <section className="campaign-overview__performance">
        <header className="campaign-overview__performance-head">
          <div className="campaign-overview__performance-eyebrow mono-eyebrow">
            Performance
          </div>
          {!sessionListEmpty && defaultDateRange && (
            <CampaignRangeSelector
              rangeType={rangeType}
              setRangeType={setRangeType}
              byCampaignRangeDate={byCampaignRangeDate}
              setByCampaignRangeDate={handleRangeChange}
              timezone={effectiveTimezone}
              defaultDateRange={defaultDateRange}
              disabled={sessionListEmpty}
            />
          )}
        </header>

        <DetailBar
          {...reportDataSummary}
          startDate={campaignOrder.startDate}
          endDate={campaignOrder.endDate}
        />

        <div className="campaign-overview__chart">
          <div className="campaign-overview__chart-head">
            <div className="campaign-overview__chart-eyebrow mono-eyebrow">
              Trend
            </div>

            <div className="campaign-overview__axis-row">
              <div className="campaign-overview__axis-field">
                <span className="campaign-overview__axis-label mono-eyebrow">
                  <span className="campaign-overview__axis-dot campaign-overview__axis-dot--left" />
                  Left axis
                </span>
                <Select
                  onSelect={(value) => setLeftYLabel(value)}
                  className="campaign-overview__axis-select"
                  value={leftYLabel}
                  getPopupContainer={(trigger) => trigger.parentNode}
                  popupClassName="mjl-ds"
                >
                  {AXIS_OPTIONS.filter((op) => op.label !== rightYLabel).map((option) => (
                    <Option key={option.key} value={option.label}>
                      <MenuItemTextColor text={startCase(option.label)} color={'#7dafff'} />
                    </Option>
                  ))}
                </Select>
              </div>

              <div className="campaign-overview__axis-field campaign-overview__axis-field--right">
                <span className="campaign-overview__axis-label mono-eyebrow">
                  <span className="campaign-overview__axis-dot campaign-overview__axis-dot--right" />
                  Right axis
                </span>
                <Select
                  onSelect={(value) => setRightYLabel(value)}
                  className="campaign-overview__axis-select"
                  value={rightYLabel}
                  getPopupContainer={(trigger) => trigger.parentNode}
                  popupClassName="mjl-ds"
                >
                  {AXIS_OPTIONS.filter((op) => op.label !== leftYLabel).map((option) => (
                    <Option key={option.key} value={option.label}>
                      <MenuItemTextColor text={startCase(option.label)} color={'#92d5a7'} />
                    </Option>
                  ))}
                </Select>
              </div>
            </div>
          </div>

          <div className="campaign-overview__chart-body">
            {sessionListEmpty || !chartRows.length ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No session data yet"
              />
            ) : (
              <DualAxisAreaChart
                dualChartData={chartRows}
                leftY={leftYLabel}
                rightY={rightYLabel}
              />
            )}
          </div>
        </div>
      </section>

      <SessionDeviceBreakdown
        data={reportDataSummary ? reportDataSummary.deviceBreakdown : null}
      />

      <SessionsTable data={reportDataSummary ? reportDataSummary.sessionList : []} />
    </div>
  );
};

export default SessionsTabView;
